/**
 * Funções de validação para Lead Time Breakdown
 */
import { VALIDATION_TYPES } from './types';
import { validateLeadTimeBreakdownHeaders } from '../../fileUtils';

export function validateHeaders(headers) {
  const validation = validateLeadTimeBreakdownHeaders(headers);
  if (!validation.valid) {
    return { 
      missingHeaders: [], 
      error: validation.error,
      workflowColumns: []
    };
  }
  return { 
    missingHeaders: [], 
    error: null,
    workflowColumns: validation.workflowColumns
  };
}

export function generateHeaderError(error) {
  return error; // validateLeadTimeBreakdownHeaders já retorna uma mensagem formatada
}

export function generateValidationError(validationErrors) {
  let errorMessage = `❌ Encontrados ${validationErrors.length} erro(s) no arquivo:\n`;
  
  // Agrupar erros por tipo
  const errorsByType = validationErrors.reduce((acc, error) => {
    acc[error.type] = acc[error.type] || [];
    acc[error.type].push(error);
    return acc;
  }, {});

  // Resumo dos tipos de erro
  const errorTypeDescriptions = {
    'missing_id': 'campo ID vazio',
    'missing_tipo': 'campo Tipo de Item vazio',
    'missing_closed_date': 'campo Closed Date vazio',
    'missing_workflow_date': 'campos de fluxo de trabalho vazios',
    'invalid_closed_date': 'Closed Date inválida',
    'invalid_workflow_date': 'datas inválidas em colunas de fluxo',
    'workflow_date_sequence_error': 'sequência de datas do fluxo incorreta',
    'workflow_date_after_closed': 'datas do fluxo posteriores ao fechamento'
  };

  Object.entries(VALIDATION_TYPES).forEach(([key, value]) => {
    if (errorsByType[value.type]) {
      const description = errorTypeDescriptions[value.type] || value.type;
      errorMessage += `• ${errorsByType[value.type].length} linha(s) com ${description}\n`;
    }
  });

  // Detalhes específicos dos erros
  errorMessage += `\n🔍 Detalhes dos erros:\n`;
  validationErrors.slice(0, 10).forEach(error => {
    errorMessage += `• ${error.message}\n`;
  });
  if (validationErrors.length > 10) {
    errorMessage += `• ... e mais ${validationErrors.length - 10} erro(s)\n`;
  }

  return errorMessage;
}

/**
 * Helper function to parse date values (Excel serial numbers or date strings)
 * @param {any} dateValue - Value to parse as date
 * @returns {Date|null} Parsed date or null if invalid
 */
function parseDate(dateValue) {
  if (!dateValue) return null;
  
  // If it's already a Date object
  if (dateValue instanceof Date) {
    return dateValue;
  }
  
  // If it's a number (Excel serial date)
  if (typeof dateValue === 'number') {
    // Importar dinamicamente para evitar dependência circular
    const { excelDateToJSDate } = require('../../dateUtils');
    return excelDateToJSDate(dateValue);
  }
  
  // If it's a string, try to parse
  if (typeof dateValue === 'string') {
    const parsed = new Date(dateValue);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  
  return null;
}

/**
 * Helper function to validate if a value is a valid date
 * @param {any} date - Value to check
 * @returns {boolean} True if valid date
 */
function isValidDate(date) {
  return date instanceof Date && !isNaN(date.getTime());
}

export function validateData(json, workflowColumns) {
  const validationErrors = [];
  const validData = [];

  json.forEach((item, index) => {
    const lineNumber = index + 2; // Linha 1 são os headers
    
    // Extract required fields - usar acesso direto ao objeto para XLSX
    const id = item.ID || item['ID'];
    const tipoItem = item['Tipo de Item'];
    const closedDateValue = item['Closed Date'];

    // Validar campos obrigatórios - específico para Lead Time Breakdown
    // ID pode ser 0, mas não pode ser null, undefined ou string vazia
    const isIdValid = id !== null && id !== undefined && id !== '';
    const isTipoItemValid = tipoItem !== null && tipoItem !== undefined && tipoItem !== '';
    const isClosedDateValid = closedDateValue !== null && closedDateValue !== undefined && closedDateValue !== '';

    if (!isIdValid) {
      validationErrors.push({
        type: VALIDATION_TYPES.MISSING_ID.type,
        line: lineNumber,
        message: VALIDATION_TYPES.MISSING_ID.getMessage(lineNumber)
      });
    }

    if (!isTipoItemValid) {
      validationErrors.push({
        type: VALIDATION_TYPES.MISSING_TIPO.type,
        line: lineNumber,
        message: VALIDATION_TYPES.MISSING_TIPO.getMessage(lineNumber, id)
      });
    }

    if (!isClosedDateValid) {
      validationErrors.push({
        type: VALIDATION_TYPES.MISSING_CLOSED_DATE.type,
        line: lineNumber,
        message: VALIDATION_TYPES.MISSING_CLOSED_DATE.getMessage(lineNumber, id)
      });
    } else {
      // Validar formato da data de fechamento
      const closedDate = parseDate(closedDateValue);
      if (!isValidDate(closedDate)) {
        validationErrors.push({
          type: VALIDATION_TYPES.INVALID_CLOSED_DATE.type,
          line: lineNumber,
          message: VALIDATION_TYPES.INVALID_CLOSED_DATE.getMessage(lineNumber, closedDateValue, id)
        });
      }
    }

    // Validar datas das colunas de fluxo - TODAS devem estar preenchidas
    const workflowDates = {};
    let hasValidWorkflowDates = false;
    
    if (workflowColumns && workflowColumns.length > 0) {
      for (const column of workflowColumns) {
        const dateValue = item[column];
        
        // Verificar se a coluna de workflow está vazia (obrigatória)
        const isWorkflowDateValid = dateValue !== null && dateValue !== undefined && dateValue !== '';
        
        if (!isWorkflowDateValid) {
          validationErrors.push({
            type: VALIDATION_TYPES.MISSING_WORKFLOW_DATE.type,
            line: lineNumber,
            message: VALIDATION_TYPES.MISSING_WORKFLOW_DATE.getMessage(lineNumber, column, id)
          });
        } else {
          // Se tem valor, validar se é uma data válida
          const parsedDate = parseDate(dateValue);
          if (!isValidDate(parsedDate)) {
            validationErrors.push({
              type: VALIDATION_TYPES.INVALID_WORKFLOW_DATE.type,
              line: lineNumber,
              message: VALIDATION_TYPES.INVALID_WORKFLOW_DATE.getMessage(lineNumber, column, dateValue, id)
            });
          } else {
            workflowDates[column] = parsedDate;
            hasValidWorkflowDates = true;
          }
        }
      }
      
      // Validar sequência das datas do fluxo (apenas se temos datas válidas)
      if (hasValidWorkflowDates) {
        // Verificar sequência cronológica para cada par de etapas consecutivas
        for (let i = 0; i < workflowColumns.length - 1; i++) {
          const currentColumn = workflowColumns[i];
          const nextColumn = workflowColumns[i + 1];
          
          const currentDate = workflowDates[currentColumn];
          const nextDate = workflowDates[nextColumn];
          
          // Se ambas as datas existem, verificar se estão em ordem
          if (currentDate && nextDate && currentDate > nextDate) {
            const currentDateStr = currentDate.toLocaleDateString('pt-BR');
            const nextDateStr = nextDate.toLocaleDateString('pt-BR');
            
            validationErrors.push({
              type: VALIDATION_TYPES.WORKFLOW_DATE_SEQUENCE_ERROR.type,
              line: lineNumber,
              message: VALIDATION_TYPES.WORKFLOW_DATE_SEQUENCE_ERROR.getMessage(
                lineNumber, currentColumn, currentDateStr, nextColumn, nextDateStr, id
              )
            });
          }
        }
        
        // Validar se alguma data do fluxo é posterior à Closed Date
        if (isClosedDateValid) {
          const closedDate = parseDate(closedDateValue);
          if (isValidDate(closedDate)) {
            Object.entries(workflowDates).forEach(([column, workflowDate]) => {
              if (workflowDate > closedDate) {
                const workflowDateStr = workflowDate.toLocaleDateString('pt-BR');
                const closedDateStr = closedDate.toLocaleDateString('pt-BR');
                
                validationErrors.push({
                  type: VALIDATION_TYPES.WORKFLOW_DATE_AFTER_CLOSED.type,
                  line: lineNumber,
                  message: VALIDATION_TYPES.WORKFLOW_DATE_AFTER_CLOSED.getMessage(
                    lineNumber, column, workflowDateStr, closedDateStr, id
                  )
                });
              }
            });
          }
        }
      }
    }

    // Se todos os campos obrigatórios estão presentes e válidos, adicionar aos dados válidos
    // Agora também verificamos se todas as colunas de workflow têm datas válidas
    const allWorkflowDatesValid = workflowColumns.every(column => workflowDates[column]);
    
    if (isIdValid && isTipoItemValid && isClosedDateValid && allWorkflowDatesValid) {
      const closedDate = parseDate(closedDateValue);
      if (isValidDate(closedDate)) {
        validData.push(item);
      }
    }
  });

  return { validationErrors, validData };
}
